import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "../api/axios";

const BrandingContext = createContext(null);

const defaultBranding = {
  identity: {
    companyName: "",
    tagline: "",
    appTitle: "",
    logoUrl: "",
    faviconUrl: "",
  },
  contact: {
    email: "",
    supportEmail: "",
    phone: "",
    alternatePhone: "",
    address: "",
    website: "",
    supportText: "24/7 Support",
    googleMapUrl: "",
  },
  social: {
    facebook: "",
    instagram: "",
    youtube: "",
    linkedin: "",
    telegram: "",
    twitter: "",
    whatsapp: "",
  },
  uiText: {
    footerText: "",
    copyrightText: "",
  },
  landingPage: {
    hero: {
      title: "",
      subtitle: "",
    },
    features: {
      title: "",
      subtitle: "",
      items: [],
    },
    stats: {
      title: "",
      subtitle: "",
      items: [],
    },
    appDownload: {
      title: "",
      subtitle: "",
      features: [],
      playStoreUrl: "",
      appStoreUrl: "",
    }
  },
};

const MAX_TEXT_LENGTH = 500;
const MAX_ADDRESS_LENGTH = 800;
const MAX_URL_LENGTH = 5000;
const PHONE_REGEX = /^\d{10}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const HTTP_URL_REGEX = /^https?:\/\/[^\s/$.?#].[^\s]*$/i;
const WHATSAPP_URL_REGEX = /^https?:\/\/(wa\.me|api\.whatsapp\.com)\/.+$/i;

const cleanText = (value, maxLength = MAX_TEXT_LENGTH) =>
  typeof value === "string" ? value.trim().slice(0, maxLength) : "";

const isValidUrl = (value) => !value || HTTP_URL_REGEX.test(value);
const isValidWhatsappUrl = (value) => !value || WHATSAPP_URL_REGEX.test(value);
const isValidEmail = (value) => !value || EMAIL_REGEX.test(value);
const isValidPhone = (value) => !value || PHONE_REGEX.test(value);

const validateBrandingData = (mergedData) => {
  const errors = {};
  const invalid = (path, message) => {
    errors[path] = message;
  };

  if (!cleanText(mergedData.identity.companyName, 120)) {
    invalid("identity.companyName", "Company name is required.");
  }
  if (!cleanText(mergedData.identity.appTitle, 120)) {
    invalid("identity.appTitle", "App title is required.");
  }

  if (!isValidEmail(mergedData.contact.email)) {
    invalid("contact.email", "Primary email is invalid.");
  }
  if (!isValidEmail(mergedData.contact.supportEmail)) {
    invalid("contact.supportEmail", "Support email is invalid.");
  }
  if (!isValidPhone(mergedData.contact.phone)) {
    invalid("contact.phone", "Primary phone is invalid.");
  }
  if (!isValidPhone(mergedData.contact.alternatePhone)) {
    invalid("contact.alternatePhone", "Alternate phone is invalid.");
  }
  if (!isValidUrl(mergedData.contact.website)) {
    invalid("contact.website", "Website URL must start with http:// or https://");
  }

  const socialKeys = ["facebook", "instagram", "youtube", "linkedin", "telegram", "twitter"];
  socialKeys.forEach((key) => {
    if (!isValidUrl(mergedData.social[key])) {
      invalid(`social.${key}`, `${key} URL must start with http:// or https://`);
    }
  });
  if (!isValidWhatsappUrl(mergedData.social.whatsapp)) {
    invalid("social.whatsapp", "WhatsApp link must be a valid wa.me/api.whatsapp.com URL.");
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

const sanitizeAndValidateBranding = (data) => {
  const mergedData = {
    ...defaultBranding,
    ...(data || {}),
    identity: { ...defaultBranding.identity, ...(data?.identity || {}) },
    contact: { ...defaultBranding.contact, ...(data?.contact || {}) },
    social: { ...defaultBranding.social, ...(data?.social || {}) },
    uiText: { ...defaultBranding.uiText, ...(data?.uiText || {}) },
  };

  const sanitized = {
    identity: {
      companyName: cleanText(mergedData.identity.companyName, 120) || defaultBranding.identity.companyName,
      tagline: cleanText(mergedData.identity.tagline, 160),
      appTitle: cleanText(mergedData.identity.appTitle, 120) || defaultBranding.identity.appTitle,
      logoUrl: isValidUrl(cleanText(mergedData.identity.logoUrl, MAX_URL_LENGTH))
        ? cleanText(mergedData.identity.logoUrl, MAX_URL_LENGTH)
        : "",
      faviconUrl: isValidUrl(cleanText(mergedData.identity.faviconUrl, MAX_URL_LENGTH))
        ? cleanText(mergedData.identity.faviconUrl, MAX_URL_LENGTH)
        : "",
    },
    contact: {
      email: isValidEmail(cleanText(mergedData.contact.email, 160)) ? cleanText(mergedData.contact.email, 160) : "",
      supportEmail: isValidEmail(cleanText(mergedData.contact.supportEmail, 160))
        ? cleanText(mergedData.contact.supportEmail, 160)
        : "",
      phone: isValidPhone(cleanText(mergedData.contact.phone, 10)) ? cleanText(mergedData.contact.phone, 10) : "",
      alternatePhone: isValidPhone(cleanText(mergedData.contact.alternatePhone, 10))
        ? cleanText(mergedData.contact.alternatePhone, 10)
        : "",
      address: cleanText(mergedData.contact.address, MAX_ADDRESS_LENGTH),
      website: isValidUrl(cleanText(mergedData.contact.website, MAX_URL_LENGTH))
        ? cleanText(mergedData.contact.website, MAX_URL_LENGTH)
        : "",
      supportText: cleanText(mergedData.contact.supportText, 180),
      googleMapUrl: cleanText(mergedData.contact.googleMapUrl, MAX_URL_LENGTH),
    },
    social: {
      facebook: isValidUrl(cleanText(mergedData.social.facebook, MAX_URL_LENGTH))
        ? cleanText(mergedData.social.facebook, MAX_URL_LENGTH)
        : "",
      instagram: isValidUrl(cleanText(mergedData.social.instagram, MAX_URL_LENGTH))
        ? cleanText(mergedData.social.instagram, MAX_URL_LENGTH)
        : "",
      youtube: isValidUrl(cleanText(mergedData.social.youtube, MAX_URL_LENGTH))
        ? cleanText(mergedData.social.youtube, MAX_URL_LENGTH)
        : "",
      linkedin: isValidUrl(cleanText(mergedData.social.linkedin, MAX_URL_LENGTH))
        ? cleanText(mergedData.social.linkedin, MAX_URL_LENGTH)
        : "",
      telegram: isValidUrl(cleanText(mergedData.social.telegram, MAX_URL_LENGTH))
        ? cleanText(mergedData.social.telegram, MAX_URL_LENGTH)
        : "",
      twitter: isValidUrl(cleanText(mergedData.social.twitter, MAX_URL_LENGTH))
        ? cleanText(mergedData.social.twitter, MAX_URL_LENGTH)
        : "",
      whatsapp: isValidWhatsappUrl(cleanText(mergedData.social.whatsapp, MAX_URL_LENGTH))
        ? cleanText(mergedData.social.whatsapp, MAX_URL_LENGTH)
        : defaultBranding.social.whatsapp,
    },
    uiText: {
      footerText: cleanText(mergedData.uiText.footerText, 400),
      copyrightText: cleanText(mergedData.uiText.copyrightText, 220),
    },
    landingPage: {
      hero: {
        title: cleanText(mergedData.landingPage?.hero?.title, 200),
        subtitle: cleanText(mergedData.landingPage?.hero?.subtitle, 500),
      },
      features: {
        title: cleanText(mergedData.landingPage?.features?.title, 200),
        subtitle: cleanText(mergedData.landingPage?.features?.subtitle, 500),
        items: (mergedData.landingPage?.features?.items || [])
          .filter(Boolean)
          .map(item => ({
            icon: cleanText(item.icon, 100),
            title: cleanText(item.title, 100),
            description: cleanText(item.description, 300),
            redirectUrl: cleanText(item.redirectUrl, MAX_URL_LENGTH),
          })),
      },
      stats: {
        title: cleanText(mergedData.landingPage?.stats?.title, 200),
        subtitle: cleanText(mergedData.landingPage?.stats?.subtitle, 500),
        items: (mergedData.landingPage?.stats?.items || [])
          .filter(Boolean)
          .map(item => ({
            icon: cleanText(item.icon, 100),
            value: cleanText(item.value, 50),
            label: cleanText(item.label, 100),
          })),
      },
      appDownload: {
        title: cleanText(mergedData.landingPage?.appDownload?.title, 200),
        subtitle: cleanText(mergedData.landingPage?.appDownload?.subtitle, 500),
        features: (mergedData.landingPage?.appDownload?.features || []).map(f => cleanText(f, 200)),
        playStoreUrl: cleanText(mergedData.landingPage?.appDownload?.playStoreUrl, MAX_URL_LENGTH),
        appStoreUrl: cleanText(mergedData.landingPage?.appDownload?.appStoreUrl, MAX_URL_LENGTH),
      }
    },
  };

  const validation = validateBrandingData(sanitized);
  return { branding: sanitized, validation };
};

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(defaultBranding);
  const [validation, setValidation] = useState({ isValid: true, errors: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await api.get("/admin/branding-settings");
        const { branding: nextBranding, validation: nextValidation } = sanitizeAndValidateBranding(
          response?.data?.data
        );
        setBranding(nextBranding);
        setValidation(nextValidation);
      } catch (error) {
        setBranding(defaultBranding);
        setValidation({ isValid: true, errors: {} });
      } finally {
        setLoading(false);
      }
    };
    fetchBranding();
  }, []);

  useEffect(() => {
    document.title = branding?.identity?.appTitle || "Dummy Classes";

    // ── Update meta description ──
    const descEl = document.getElementById("meta-description")
      || document.querySelector('meta[name="description"]');
    if (descEl) {
      descEl.setAttribute("content", branding?.identity?.tagline || "");
    }

    // ── Open Graph tags ──
    const companyName = branding?.identity?.companyName || "";
    const tagline = branding?.identity?.tagline || "";
    const logoUrl = branding?.identity?.logoUrl || "";

    const ogUpdates = {
      "og-title": companyName,
      "og-description": tagline,
      "og-image": logoUrl,
      "og-site-name": companyName,
      "twitter-title": companyName,
      "twitter-description": tagline,
      "twitter-image": logoUrl,
    };

    Object.entries(ogUpdates).forEach(([id, value]) => {
      const el = document.getElementById(id);
      if (el) el.setAttribute("content", value);
    });
  }, [branding?.identity?.appTitle, branding?.identity?.companyName, branding?.identity?.tagline, branding?.identity?.logoUrl]);

  useEffect(() => {
    const faviconUrl = branding?.identity?.faviconUrl;
    if (!faviconUrl) return;

    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "icon");
      document.head.appendChild(link);
    }

    // Use hash-based cache busting to avoid breaking presigned URL signatures.
    link.setAttribute("href", `${faviconUrl}#cb=${Date.now()}`);
  }, [branding?.identity?.faviconUrl]);

  const value = useMemo(
    () => ({
      branding,
      validation,
      loading,
      refreshBranding: async () => {
        const response = await api.get("/admin/branding-settings");
        const { branding: nextBranding, validation: nextValidation } = sanitizeAndValidateBranding(
          response?.data?.data
        );
        setBranding(nextBranding);
        setValidation(nextValidation);
      },
    }),
    [branding, validation, loading]
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error("useBranding must be used within BrandingProvider");
  }
  return context;
};
